import { useState, useEffect } from "react";
import axios from "axios";
import { MailWarning } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

function fmtWait(m) {
  if (m < 60) return `${m}m`;
  if (m < 1440) return `${Math.floor(m / 60)}h ${m % 60}m`;
  const d = Math.floor(m / 1440);
  const h = Math.floor((m % 1440) / 60);
  return `${d}d ${h}h`;
}

function tierBadge(tier) {
  if (tier >= 60) return { label: "60m", bg: "bg-red-500/20", text: "text-red-400", ring: "ring-red-500/30" };
  if (tier >= 30) return { label: "30m", bg: "bg-orange-500/20", text: "text-orange-400", ring: "ring-orange-500/30" };
  if (tier >= 15) return { label: "15m", bg: "bg-yellow-500/20", text: "text-yellow-400", ring: "ring-yellow-500/30" };
  return { label: "<15m", bg: "bg-slate-500/20", text: "text-slate-400", ring: "ring-slate-500/30" };
}

function StatusBadge({ status }) {
  const resolved = status === "resolved";
  return (
    <span
      data-testid={`email-status-${status}`}
      className={`px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wide ${
        resolved ? "bg-emerald-500/15 text-emerald-400" : "bg-red-500/15 text-red-400 animate-pulse"
      }`}
    >
      {status}
    </span>
  );
}

export function UnresolvedEmail() {
  const [items, setItems] = useState([]);

  useEffect(() => {
    const poll = async () => {
      try {
        const { data } = await axios.get(`${API}/email/unresolved`);
        setItems(data.items || []);
      } catch {}
    };
    poll();
    const iv = setInterval(poll, 30000);
    return () => clearInterval(iv);
  }, []);

  const count = items.filter((i) => i.status === "unresolved").length;

  return (
    <div data-testid="unresolved-email-section" className="rounded border border-amber-500/20 bg-amber-950/10 overflow-hidden h-full flex flex-col">
      <div className="flex items-center gap-1.5 px-2 py-1">
        <MailWarning className="w-3 h-3 text-amber-400" strokeWidth={2} />
        <span className="font-display text-[10px] font-bold tracking-wider text-amber-400 uppercase">Unresolved Email</span>
        <span data-testid="unresolved-email-count" className="text-[9px] font-bold px-1 py-0.5 rounded bg-amber-500/15 text-amber-400">{count}</span>
        {items.length === 0 && <span className="text-[10px] text-slate-600 ml-2">No unanswered emails</span>}
      </div>
      {items.length > 0 && (
        <div className="overflow-x-auto overflow-y-auto flex-1">
          <table className="w-full text-[10px]">
            <thead>
              <tr className="text-slate-500 uppercase tracking-wider border-t border-white/[0.04]">
                <th className="px-2 py-0.5 text-left font-bold">Ticket</th>
                <th className="px-1.5 py-0.5 text-left font-bold">Customer</th>
                <th className="px-1.5 py-0.5 text-right font-bold">Wait</th>
                <th className="px-1.5 py-0.5 text-left font-bold">Subject</th>
                <th className="px-1.5 py-0.5 text-center font-bold">SLA</th>
                <th className="px-2 py-0.5 text-center font-bold">Status</th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => {
                const t = tierBadge(item.sla_tier);
                const resolved = item.status === "resolved";
                return (
                  <tr
                    key={item.conversation_id}
                    data-testid={`email-row-${item.sender}`}
                    className={`border-t border-white/[0.03] ${resolved ? "opacity-40" : ""}`}
                  >
                    <td className="px-2 py-0.5 font-bold text-white whitespace-nowrap">
                      {item.ticket_number || <span className="text-amber-500/70 text-[9px] font-bold">FOLLOW-UP</span>}
                    </td>
                    <td className="px-1.5 py-0.5 text-slate-300 truncate max-w-[120px]" title={item.sender}>
                      {item.customer_name || item.sender_name || item.sender}
                    </td>
                    <td className="px-1.5 py-0.5 text-right font-mono text-white whitespace-nowrap">
                      {fmtWait(item.wait_minutes)}
                    </td>
                    <td className="px-1.5 py-0.5 text-slate-400 truncate max-w-[180px]" title={item.subject}>
                      {item.subject || "\u2014"}
                    </td>
                    <td className="px-1.5 py-0.5 text-center">
                      <span className={`inline-block px-1 py-0.5 rounded text-[9px] font-bold ring-1 ${t.bg} ${t.text} ${t.ring}`}>{t.label}</span>
                    </td>
                    <td className="px-2 py-0.5 text-center"><StatusBadge status={item.status} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
